import React from 'react';
import classnames from 'classnames';

import { BUTTON_SIZES } from '@buyside/components/presentation/Button/button';
import FollowButton, {
  FollowableType
} from '@buyside/components/presentation/FollowButton/followButton';
import ProfileImage from '@buyside/components/presentation/ProfileImage/profileImage';

import {
  AumRange,
  EmployerProfile,
  EmployerReturn,
  FundStrategy,
  FundType,
  Maybe
} from '@interfaces/graphql-typings';

import { formatToPercentage } from '@buyside/util/textHelpers';

import styles from './onboardingCommunitySuggestionCardStyles.scss';

const OnboardingFundRecommendationCard: React.FC<{
  fund: Pick<
    EmployerProfile,
    | 'id'
    | 'aumRange'
    | 'focus'
    | 'foundingYear'
    | 'fundType'
    | 'name'
    | 'strategy'
    | 'logoProfileUrl'
  > & { lastReturn: Maybe<Pick<EmployerReturn, 'ltm' | 'sinceInception' | 'ytd'>> };
  preferredFundTypes: Maybe<FundType['name']>[];
  preferredStrategies: Maybe<FundStrategy['name']>[];
  preferredAumRanges: Maybe<AumRange['name']>[];
}> = ({
  fund: { id, aumRange, focus, foundingYear, fundType, lastReturn, logoProfileUrl, name, strategy },
  preferredAumRanges,
  preferredFundTypes,
  preferredStrategies
}) => {
  const matchedCriteria = [
    preferredFundTypes.find(el => el === fundType),
    preferredStrategies.find(el => el === strategy),
    preferredAumRanges.find(el => el === aumRange)
  ].filter(Boolean);

  return (
    <div className={styles.communityCardContainer}>
      <div className={styles.communityCard}>
        <div>
          <div className={styles.followButtonContainer}>
            <FollowButton size={BUTTON_SIZES.TINIEST} id={id} type={FollowableType.FUND} key={id} />
          </div>
          <ProfileImage source={logoProfileUrl} className={styles.fundImage} />
        </div>
        <div>
          <div>
            <div className={styles.fundName}>
              <div>{name}</div>
            </div>
            <div className={classnames(styles.fundTypeList, styles.profileImage)}>
              <div className={styles.employerName}>{fundType}</div>
              <div className={styles.employerName}>{strategy}</div>
              <div className={styles.employerName}>{aumRange}</div>
            </div>
            <div className={styles.watchlistedSectors}>
              Based on your fund preference{matchedCriteria.length === 1 ? '' : 's'}:{' '}
              <div className={styles.sectors}>
                {matchedCriteria.map(matched => (
                  <div key={matched} className={styles.sectorCard}>
                    {matched}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className={styles.fundDetails}>
            <div className={styles.fundDetailItem}>
              <div>Return LTM</div>
              <div>{formatToPercentage(lastReturn?.ltm)}</div>
            </div>
            <div className={styles.fundDetailItem}>
              <div>Return YTD</div>
              <div>{formatToPercentage(lastReturn?.ytd)}</div>
            </div>
            <div className={styles.fundDetailItem}>
              <div>Return since {foundingYear}</div>
              <div>{formatToPercentage(lastReturn?.sinceInception)}</div>
            </div>
            <div className={styles.fundDetailItem}>
              <div>Focus</div>
              <div>{focus || 'N/A'}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingFundRecommendationCard;
